import { useState } from 'react';
import { Layout } from '../components/Layout';
import { exportPDF, exportCSV, downloadBlob, type ExportSections } from '../api/export';

const SECTION_OPTIONS = [
  { key: 'profile', label: 'Profilo Utente', description: 'Dati anagrafici e fisici', icon: '👤' },
  { key: 'medications', label: 'Farmaci', description: 'Farmaci attivi e passati', icon: '💊' },
  { key: 'familyHistory', label: 'Storia Familiare', description: 'Condizioni familiari', icon: '👨‍👩‍👧‍👦' },
  { key: 'healthNotes', label: 'Note Salute', description: 'Annotazioni e osservazioni', icon: '📝' },
  { key: 'measurements', label: 'Misurazioni', description: 'Peso, pressione, ecc.', icon: '📊' },
  { key: 'wearable', label: 'Dati Wearable', description: 'Sonno e metriche giornaliere', icon: '⌚' },
  { key: 'labParameters', label: 'Parametri Lab', description: 'Analisi di laboratorio', icon: '🧪' },
] as const;

type SectionKey = typeof SECTION_OPTIONS[number]['key'];

export function Export() {
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Filename state
  const [filename, setFilename] = useState('report_salute');

  // Sections state
  const [sections, setSections] = useState<ExportSections>({
    profile: true,
    medications: true,
    familyHistory: true,
    healthNotes: true,
    measurements: true,
    wearable: true,
    labParameters: true,
  });

  // Loading state
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'csv' | null>(null);

  const toggleSection = (key: SectionKey) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAll = () => {
    setSections({
      profile: true,
      medications: true,
      familyHistory: true,
      healthNotes: true,
      measurements: true,
      wearable: true,
      labParameters: true,
    });
  };

  const deselectAll = () => {
    setSections({
      profile: false,
      medications: false,
      familyHistory: false,
      healthNotes: false,
      measurements: false,
      wearable: false,
      labParameters: false,
    });
  };

  const setDateRange = (months: number | 'all') => {
    const end = new Date();
    setEndDate(end.toISOString().split('T')[0]);

    if (months === 'all') {
      const start = new Date(2000, 0, 1);
      setStartDate(start.toISOString().split('T')[0]);
    } else {
      const start = new Date();
      start.setMonth(start.getMonth() - months);
      setStartDate(start.toISOString().split('T')[0]);
    }
  };

  const handleExport = async (type: 'pdf' | 'csv') => {
    // Check if at least one section is selected
    if (!Object.values(sections).some((v) => v)) {
      alert('Seleziona almeno una sezione da esportare');
      return;
    }

    setIsExporting(true);
    setExportType(type);

    try {
      const options = {
        startDate,
        endDate,
        filename,
        sections,
      };

      const blob = type === 'pdf'
        ? await exportPDF(options)
        : await exportCSV(options);

      const extension = type === 'pdf' ? '.pdf' : '.csv';
      const finalFilename = filename.trim() || 'report_salute';
      downloadBlob(blob, `${finalFilename}${extension}`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Errore durante l\'esportazione. Riprova.');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const selectedCount = Object.values(sections).filter(Boolean).length;

  return (
    <Layout title="Esporta Dati">
      <div className="max-w-4xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div>
          <p className="text-lg text-gray-600">
            Genera un report PDF o CSV dei tuoi dati sanitari
          </p>
        </div>

        {/* Period and Filename */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Period card */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-2">Periodo</h3>
            <p className="text-gray-500 mb-4">Seleziona l'intervallo di date per i dati temporali</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Da</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">A</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setDateRange(1)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Ultimo mese
              </button>
              <button
                onClick={() => setDateRange(3)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                3 mesi
              </button>
              <button
                onClick={() => setDateRange(6)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                6 mesi
              </button>
              <button
                onClick={() => setDateRange(12)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                1 anno
              </button>
              <button
                onClick={() => setDateRange('all')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Tutto
              </button>
            </div>
          </div>

          {/* Filename card */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-semibold text-lg text-gray-900 mb-2">Nome File</h3>
            <p className="text-gray-500 mb-4">Opzionale - verrà usato un nome predefinito se vuoto</p>

            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="report_salute"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-2">
              L'estensione verrà aggiunta automaticamente (.pdf o .csv)
            </p>
          </div>
        </div>

        {/* Sections card */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg text-gray-900">Dati da Esportare</h3>
              <p className="text-gray-500">Seleziona quali sezioni includere nel report</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Seleziona tutto
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Deseleziona tutto
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTION_OPTIONS.map((option) => (
              <button
                key={option.key}
                onClick={() => toggleSection(option.key)}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                  sections[option.key]
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                    sections[option.key]
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {sections[option.key] ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </div>
                <span className="text-2xl">{option.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500 truncate">{option.description}</p>
                </div>
              </button>
            ))}
          </div>

          <p className="text-sm text-gray-500 mt-4">
            {selectedCount} di {SECTION_OPTIONS.length} sezioni selezionate
          </p>
        </div>

        {/* Export buttons */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleExport('pdf')}
              disabled={isExporting || selectedCount === 0}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg font-medium"
            >
              {isExporting && exportType === 'pdf' ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generazione...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Esporta PDF
                </>
              )}
            </button>

            <button
              onClick={() => handleExport('csv')}
              disabled={isExporting || selectedCount === 0}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg font-medium"
            >
              {isExporting && exportType === 'csv' ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generazione...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Esporta CSV
                </>
              )}
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            Il file verrà scaricato automaticamente nella cartella Download
          </p>
        </div>
      </div>
    </Layout>
  );
}
